import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mic, Brain, Zap, Users, BarChart3, Sparkles, Shield, ChevronRight } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import Orb from "@/components/Orb";

const Landing = () => {
  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden" data-testid="landing-root">
      {/* aurora background */}
      <div className="holo-mesh" aria-hidden />

      {/* sticky nav */}
      <nav className="sticky top-0 z-40 backdrop-blur-2xl bg-black/60 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <Link to="/" data-testid="brand-link"><Wordmark /></Link>
          <div className="hidden md:flex items-center gap-8 text-[0.86rem]">
            <a href="#capabilities" className="nav-link" data-testid="nav-features">Capabilities</a>
            <a href="#surface" className="nav-link" data-testid="nav-surface">Surface</a>
            <Link to="/pricing" className="nav-link" data-testid="nav-pricing">Pricing</Link>
            <Link to="/login" className="nav-link" data-testid="nav-login">Sign in</Link>
          </div>
          <Link to="/signup" data-testid="nav-signup">
            <button className="btn-primary text-[0.82rem] py-2 px-4">Get started</button>
          </Link>
        </div>
      </nav>

      {/* hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-32">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 fade-up">
            <div className="label-mono mb-8 inline-flex items-center gap-3">
              <span className="pulse-dot" /> v1 · study operating system
            </div>
            <h1 className="font-display text-[3.25rem] sm:text-[4.25rem] lg:text-[5.5rem] leading-[0.94] tightest font-semibold mb-8">
              The quietest way<br/>
              to <span className="chrome-text">master</span><br/>
              every lesson.
            </h1>
            <p className="text-lg text-neutral-400 max-w-lg mb-10 leading-relaxed">
              Vertisiya listens through your day, transcribes every lecture, and quietly turns each period into notes, flashcards, worksheets and practice tests — filed exactly where your timetable expects them.
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <Link to="/signup" data-testid="hero-cta-signup">
                <button className="btn-primary">Start free <ArrowUpRight size={16}/></button>
              </Link>
              <Link to="/pricing" data-testid="hero-cta-pricing">
                <button className="btn-secondary">See pricing <ChevronRight size={14}/></button>
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-10 text-[0.75rem] label-mono">
              <span>5 free recordings / mo</span>
              <span>no card required</span>
            </div>
          </div>

          {/* orb hero */}
          <div className="md:col-span-6 relative flex items-center justify-center">
            <div className="relative">
              <Orb size={460} />
              {/* floating capture chip */}
              <div className="absolute -bottom-4 -left-6 md:-left-10 glass px-4 py-3 fade-up backdrop-blur-2xl" style={{ animationDelay: "0.3s" }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/[0.06] border border-white/10 flex items-center justify-center rounded-[2px]">
                    <Mic size={14} className="text-amber-400" />
                  </div>
                  <div>
                    <div className="text-[0.8rem] font-medium">Physics · Period 3</div>
                    <div className="text-[0.7rem] text-neutral-500 tabular">47:12 · transcribing</div>
                  </div>
                </div>
              </div>
              {/* floating output chip */}
              <div className="absolute -top-3 -right-4 md:-right-8 glass px-4 py-3 fade-up backdrop-blur-2xl" style={{ animationDelay: "0.55s" }}>
                <div className="text-[0.7rem] label-mono mb-1">Generated</div>
                <div className="text-[0.8rem]">14 flashcards · 1 test</div>
              </div>
            </div>
          </div>
        </div>

        {/* trust strip */}
        <div className="mt-24 pt-8 border-t border-white/[0.06] grid grid-cols-2 md:grid-cols-4 gap-8 text-neutral-500 text-[0.8rem]">
          <Stat n="47%" k="avg retention lift" />
          <Stat n="9.2s" k="lesson → notes" />
          <Stat n="6" k="artifact generators" />
          <Stat n="0" k="lock-in" />
        </div>
      </section>

      {/* surface / bento */}
      <section id="surface" className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-32">
        <div className="mb-14 max-w-3xl">
          <div className="label-mono mb-4">/ surface</div>
          <h2 className="font-display text-3xl md:text-5xl tightest font-semibold leading-[1.02]">
            A control room for your <span className="chrome-text">study day</span>.
          </h2>
        </div>

        <div className="grid md:grid-cols-12 gap-3">
          {/* main preview panel */}
          <div className="md:col-span-8 glass p-8 relative overflow-hidden min-h-[380px]">
            <div className="flex items-center justify-between mb-6">
              <div className="label-mono">/ dashboard</div>
              <div className="flex items-center gap-2 text-[0.7rem] text-neutral-500">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> live
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["Physics", "Chemistry", "History", "Maths", "Biology", "English"].map((s, i) => (
                <div key={s} className="border border-white/[0.06] p-4 hover:border-white/20 transition-colors" style={{ background: "rgba(255,255,255,0.015)" }}>
                  <div className="text-[0.7rem] text-neutral-500 mb-2">Period {i+1}</div>
                  <div className="text-sm font-medium">{s}</div>
                  <div className="mt-3 text-[0.7rem] text-neutral-500 tabular">{Math.floor(2+i*1.4)} recordings</div>
                </div>
              ))}
            </div>
          </div>

          {/* live capture */}
          <div className="md:col-span-4 glass p-8 relative overflow-hidden min-h-[380px] flex flex-col">
            <div className="label-mono mb-6">/ live capture</div>
            <div className="flex-1 flex items-center justify-center">
              <Orb size={200} active />
            </div>
            <div className="mt-6 space-y-1.5 font-mono text-[0.72rem] text-neutral-500">
              <div className="scan-line py-0.5">→ transcribing audio</div>
              <div>→ segmenting by topic</div>
              <div className="text-amber-500">→ 14 flashcards ready</div>
            </div>
          </div>

          {/* smaller bento cards */}
          <BentoCard className="md:col-span-4" icon={<Brain size={16}/>} title="GPT-5.2 generators" desc="Notes, flashcards, worksheets, study guides, practice tests — instant." />
          <BentoCard className="md:col-span-4" icon={<Zap size={16}/>} title="Smart timetable" desc="Detects period length from your day. Drag, drop, merge with +1." />
          <BentoCard className="md:col-span-4" icon={<Users size={16}/>} title="@username invites" desc="Study circles with accept / decline. No spam, no noise." />
          <BentoCard className="md:col-span-6" icon={<Sparkles size={16}/>} title="1:1 AI Study Coach" desc="A socratic tutor that knows every transcript. Starter and above." />
          <BentoCard className="md:col-span-3" icon={<BarChart3 size={16}/>} title="Analytics (Pro)" desc="Mastery signals per subject." />
          <BentoCard className="md:col-span-3" icon={<Shield size={16}/>} title="Private items" desc="Locked flashcards on paid plans." />
        </div>
      </section>

      {/* capabilities strip */}
      <section id="capabilities" className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-32">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-5">
            <div className="label-mono mb-4">/ how it feels</div>
            <h2 className="font-display text-3xl md:text-4xl tightest font-semibold leading-[1.05]">
              Press record.<br/>Everything else is <span className="chrome-text">already done.</span>
            </h2>
          </div>
          <ol className="md:col-span-7 space-y-6">
            {[
              ["01", "Set your timetable once", "We infer period length from your start & end times. No more manual grids."],
              ["02", "Record inside the app", "Browser-native capture, WebM audio, background upload. Live orb visualiser."],
              ["03", "Get every artifact", "Whisper transcribes → GPT-5.2 generates. Filed automatically by subject."],
              ["04", "Study with intent", "Flashcards, tests, and a coach that remembers every lesson you've had."],
            ].map(([n, t, d]) => (
              <li key={n} className="flex gap-6 border-t border-white/[0.06] pt-6">
                <div className="font-mono text-neutral-600 text-sm w-8 shrink-0">{n}</div>
                <div>
                  <div className="text-base font-medium mb-1">{t}</div>
                  <div className="text-[0.86rem] text-neutral-500 leading-relaxed">{d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-32">
        <div className="glass-strong p-10 md:p-16 relative overflow-hidden">
          <div className="holo-mesh opacity-60" aria-hidden />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="label-mono mb-4">/ start</div>
              <h3 className="font-display text-3xl md:text-5xl tightest font-semibold leading-[1] mb-4">
                Your first lesson<br/>on the house.
              </h3>
              <p className="text-neutral-400 max-w-md">Free tier. 5 recordings a month. No card. Cancel by walking away.</p>
            </div>
            <div className="flex md:justify-end gap-3">
              <Link to="/signup" data-testid="cta-signup">
                <button className="btn-primary">Start free <ArrowUpRight size={16}/></button>
              </Link>
              <Link to="/pricing" data-testid="cta-pricing">
                <button className="btn-secondary">See pricing</button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* footer */}
      <footer className="relative z-10 border-t border-white/[0.06] max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <Wordmark size="sm" />
            <span className="text-[0.75rem] text-neutral-600">© 2026 · built for relentless learners</span>
          </div>
          <div className="flex gap-6 text-[0.8rem] text-neutral-500">
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/login" className="hover:text-white transition-colors">Sign in</Link>
            <a href="#capabilities" className="hover:text-white transition-colors">Capabilities</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Stat = ({ n, k }) => (
  <div>
    <div className="font-display text-3xl font-semibold text-white tightest tabular">{n}</div>
    <div className="text-[0.72rem] text-neutral-500 uppercase tracking-[0.16em] mt-1">{k}</div>
  </div>
);

const BentoCard = ({ icon, title, desc, className }) => (
  <div className={`glass p-6 group transition-colors hover:border-white/20 ${className}`}>
    <div className="w-8 h-8 flex items-center justify-center bg-white/[0.04] border border-white/[0.08] mb-4 text-neutral-300 group-hover:text-amber-500 transition-colors rounded-[2px]">
      {icon}
    </div>
    <div className="font-medium text-[0.95rem] mb-1.5 tracking-tight">{title}</div>
    <p className="text-neutral-500 text-[0.82rem] leading-relaxed">{desc}</p>
  </div>
);

export default Landing;
